import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Input,
  SegmentGroup,
  Select,
  Switch,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex } from "styled-system/jsx";
import { DateInput, WhereaboutsModal } from "shared/components";
import { formatPrice } from "shared/utils";
import {
  classifyPaymentOutcome,
  getPaymentFieldErrors,
  type PaymentFieldErrors,
} from "./invoicePaymentValidation";
import { InvoiceJournalContent } from "./InvoiceJournalContent";

const RECORD_PAYMENT = gql`
  mutation InvoiceRecordPayment(
    $paymentRequestId: MongoID!
    $version: Int!
    $amount: Int!
    $method: String!
    $receivedDate: String!
    $accountingDate: String
    $payer: String
    $reference: String
    $chequeNumber: String
    $processorReference: String
    $cardBrand: String
    $cardLast4: String
    $note: String
    $idempotencyKey: String
    $notifyCustomer: Boolean
  ) {
    paymentRequestRecordPayment(
      paymentRequestId: $paymentRequestId
      version: $version
      amount: $amount
      method: $method
      receivedDate: $receivedDate
      accountingDate: $accountingDate
      payer: $payer
      reference: $reference
      chequeNumber: $chequeNumber
      processorReference: $processorReference
      cardBrand: $cardBrand
      cardLast4: $cardLast4
      note: $note
      idempotencyKey: $idempotencyKey
      notifyCustomer: $notifyCustomer
    ) {
      _id
      status
      version
      order
      amountPaid
      balanceDue
      creditBalance
      balanceStatus
    }
  }
`;

const PAYMENT_DETAILS = gql`
  query InvoiceRecordPaymentDetails($paymentRequestId: MongoID) {
    paymentRequestById(paymentRequestId: $paymentRequestId) {
      _id
      version
      status
      balanceDue
      currency
    }
  }
`;

const STANDARD_PAYMENT_METHODS = [
  { label: "Cheque", value: "CHEQUE" },
  { label: "Cash", value: "CASH" },
  { label: "Bank transfer", value: "BANK_TRANSFER" },
  { label: "EFT", value: "EFT" },
  { label: "Interac", value: "INTERAC" },
  { label: "Wire transfer", value: "WIRE_TRANSFER" },
  { label: "Offline Debit", value: "OFFLINE_DEBIT" },
  { label: "Card (record only)", value: "CARD" },
  { label: "Other", value: "OTHER" },
];

const COMPLIMENTARY_METHOD = {
  label: "Complimentary",
  value: "COMPLIMENTARY",
};

// Stored as a $0.00 Complimentary entry, but nothing is written off, so it
// isn't presented (or tagged) as a comp.
const NO_PAYMENT_REQUIRED_METHOD = {
  label: "No payment required ($0.00)",
  value: "COMPLIMENTARY",
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  paymentRequestId: string;
  onCompleted: () => void | Promise<void>;
  /** Settle an unsent draft in full without emailing the invoice. Defaults to Complimentary. */
  settleWithoutSending?: boolean;
};

type ModalTab = "PAYMENT" | "JOURNAL";

const today = () => new Date().toISOString().slice(0, 10);

const invalidInputProps = (message?: string) =>
  message
    ? {
        hint: message,
        "data-invalid": true as const,
        "aria-invalid": true as const,
      }
    : {};

export const InvoiceRecordPaymentModal = ({
  isOpen,
  onClose,
  paymentRequestId,
  onCompleted,
  settleWithoutSending = false,
}: Props) => {
  const { toastFactory } = useToastContext();
  const [activeTab, setActiveTab] = useState<ModalTab>("PAYMENT");
  // Set when a payment is voided from the Journal tab, so the parent is
  // notified once on close instead of refetching (and unmounting) mid-view.
  const hasPendingJournalChangesRef = useRef(false);
  const { data: paymentDetailsData, loading: paymentDetailsLoading } = useQuery(
    PAYMENT_DETAILS,
    {
      variables: { paymentRequestId },
      skip: !isOpen,
      fetchPolicy: "network-only",
    },
  );
  const paymentDetails = paymentDetailsData?.paymentRequestById;
  const balanceDue = paymentDetails?.balanceDue ?? 0;
  const currency = paymentDetails?.currency ?? "cad";
  // Nothing owed (e.g. a $0.00 invoice): Complimentary is the settlement path.
  const isZeroBalance = !!paymentDetails && balanceDue <= 0;
  const defaultMethod =
    isZeroBalance || settleWithoutSending ? "COMPLIMENTARY" : "CHEQUE";
  const [amount, setAmount] = useState("0.00");
  const [method, setMethod] = useState(defaultMethod);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [receivedDate, setReceivedDate] = useState(today);
  const [accountingDate, setAccountingDate] = useState("");
  const [payer, setPayer] = useState("");
  const [reference, setReference] = useState("");
  const [chequeNumber, setChequeNumber] = useState("");
  const [processorReference, setProcessorReference] = useState("");
  const [cardBrand, setCardBrand] = useState("");
  const [cardLast4, setCardLast4] = useState("");
  const [note, setNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState<PaymentFieldErrors>({});
  const idempotencyKey = useRef(crypto.randomUUID());
  const isComplimentary = method === "COMPLIMENTARY";
  const methodCollection = useMemo(
    () =>
      Select.createCollection({
        items: isZeroBalance
          ? [NO_PAYMENT_REQUIRED_METHOD]
          : [...STANDARD_PAYMENT_METHODS, COMPLIMENTARY_METHOD],
      }),
    [isZeroBalance],
  );
  const [recordPayment, { loading }] = useMutation(RECORD_PAYMENT, {
    refetchQueries: ["StorefrontOrderInvoiceFeed", "OrderMany"],
  });

  useEffect(() => {
    if (!isOpen) return;
    setAmount(isZeroBalance ? "0.00" : (balanceDue / 100).toFixed(2));
    setMethod(defaultMethod);
    setNotifyCustomer(true);
    setFieldErrors({});
  }, [balanceDue, defaultMethod, isOpen, isZeroBalance]);

  useEffect(() => {
    if (isOpen) setActiveTab("PAYMENT");
  }, [isOpen]);

  const resetAndClose = () => {
    setAmount((balanceDue / 100).toFixed(2));
    setMethod(defaultMethod);
    setNotifyCustomer(true);
    setReceivedDate(today());
    setAccountingDate("");
    setPayer("");
    setReference("");
    setChequeNumber("");
    setProcessorReference("");
    setCardBrand("");
    setCardLast4("");
    setNote("");
    setFieldErrors({});
    idempotencyKey.current = crypto.randomUUID();
    onClose();
    if (hasPendingJournalChangesRef.current) {
      hasPendingJournalChangesRef.current = false;
      void onCompleted();
    }
  };

  // A comp writes off whatever is still owed; nothing owed means a $0 settlement.
  const complimentaryAmountCents = isZeroBalance ? 0 : balanceDue;
  const amountCents =
    isComplimentary || settleWithoutSending
      ? complimentaryAmountCents
      : Math.round(Number(amount) * 100);
  const outcomeType = classifyPaymentOutcome(amountCents, balanceDue);
  // Only a payment that settles the invoice creates the order and its
  // confirmation email, so that's the only case the switch applies to.
  const settlesInvoice =
    paymentDetails?.status !== "PAID" &&
    (isComplimentary || amountCents >= balanceDue);
  const outcome = isComplimentary
    ? isZeroBalance
      ? "Nothing is owed, so this marks the invoice as paid."
      : `Comps the full $${formatPrice(balanceDue)} balance and marks this invoice as paid. Line items keep their original prices.`
    : outcomeType === "OVERPAYMENT"
      ? `This creates a ${currency.toUpperCase()} $${formatPrice(amountCents - balanceDue)} credit balance.`
      : outcomeType === "PARTIAL"
        ? `The remaining balance will be $${formatPrice(balanceDue - amountCents)}.`
        : "This pays the invoice in full.";

  const clearFieldError = (field: keyof PaymentFieldErrors) => {
    setFieldErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submit = async () => {
    if (!paymentDetails) return;
    const errors = getPaymentFieldErrors({
      amountCents,
      method,
      receivedDate: isComplimentary ? undefined : receivedDate,
      chequeNumber,
      cardLast4,
      isZeroBalance,
      balanceDue,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      await recordPayment({
        variables: isComplimentary
          ? {
              paymentRequestId,
              version: paymentDetails.version,
              amount: complimentaryAmountCents,
              method: "COMPLIMENTARY",
              receivedDate: accountingDate || today(),
              accountingDate: accountingDate || undefined,
              note: note.trim() || undefined,
              idempotencyKey: idempotencyKey.current,
              notifyCustomer,
            }
          : {
              paymentRequestId,
              version: paymentDetails.version,
              amount: amountCents,
              method,
              receivedDate,
              accountingDate: accountingDate || undefined,
              payer: payer.trim() || undefined,
              reference: reference.trim() || undefined,
              chequeNumber: chequeNumber.trim() || undefined,
              processorReference: processorReference.trim() || undefined,
              cardBrand: cardBrand.trim() || undefined,
              cardLast4: cardLast4.trim() || undefined,
              note: note.trim() || undefined,
              idempotencyKey: idempotencyKey.current,
              notifyCustomer,
            },
      });
      toastFactory.create({
        title: isComplimentary
          ? isZeroBalance
            ? "Invoice settled"
            : "Invoice comped"
          : settleWithoutSending
            ? "Invoice settled"
            : "Payment recorded",
      });
      resetAndClose();
      await onCompleted();
    } catch (error: unknown) {
      toastFactory.create({
        title: "Could not record payment",
        description: error instanceof Error ? error.message : undefined,
        type: "error",
      });
    }
  };

  const isJournalTab = activeTab === "JOURNAL";

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={resetAndClose}
      isMaxZIndex
      title={settleWithoutSending ? "Settle without sending" : "Record payment"}
      description={
        isJournalTab
          ? "Immutable invoice, payment, email, and accounting history."
          : settleWithoutSending
            ? "The invoice won't be emailed. Choose how the full balance was settled."
            : isComplimentary
              ? isZeroBalance
                ? "This invoice is $0.00. Settle it to create the order."
                : "Comp this invoice. The outstanding balance is written off as complimentary."
              : "Record the funds received. Full, partial, and overpayments are calculated automatically."
      }
      cancelButtonProps={{ onClick: resetAndClose }}
      saveButtonCopy={settleWithoutSending ? "Settle invoice" : "Record payment"}
      saveButtonProps={{
        onClick: () => void submit(),
        isLoading: loading || paymentDetailsLoading,
        disabled: !paymentDetails,
      }}
      customFooter={
        isJournalTab ? (
          <Flex
            px={{ base: "300", md: "500" }}
            py={{ base: "300", md: "400" }}
            justifyContent="flex-end"
          >
            <Button
              size="lg"
              visual="primary"
              mode="light"
              onClick={resetAndClose}
            >
              Close
            </Button>
          </Flex>
        ) : undefined
      }
    >
      <Flex flexDir="column" gap="200">
        <Box>
          <SegmentGroup.Root
            segmentStyle="buttons"
            orientation="horizontal"
            value={activeTab}
            onValueChange={value => setActiveTab(value.value as ModalTab)}
          >
            <SegmentGroup.Item value="PAYMENT">Payment</SegmentGroup.Item>
            <SegmentGroup.Item value="JOURNAL">Journal</SegmentGroup.Item>
          </SegmentGroup.Root>
        </Box>
        {isJournalTab ? (
          <InvoiceJournalContent
            paymentRequestId={paymentRequestId}
            skip={!isOpen}
            onVoided={() => {
              hasPendingJournalChangesRef.current = true;
            }}
          />
        ) : (
          <>
            <Input
              type="number"
              min={isComplimentary ? "0" : "0.01"}
              step="0.01"
              label={`Amount (${currency.toUpperCase()}) (required)`}
              value={
                isComplimentary || settleWithoutSending
                  ? (complimentaryAmountCents / 100).toFixed(2)
                  : amount
              }
              onChange={event => {
                setAmount(event.target.value);
                clearFieldError("amount");
              }}
              disabled={isComplimentary || settleWithoutSending}
              {...invalidInputProps(fieldErrors.amount)}
            />
            <Text visual="smallRegular" color="text.mid">
              {outcome}
            </Text>
            <Select.Root
              label="Payment method (required)"
              collection={methodCollection}
              value={[method]}
              onValueChange={({ value }: { value: string[] }) => {
                const next =
                  value[0] ?? (isZeroBalance ? "COMPLIMENTARY" : "OTHER");
                setMethod(next);
                clearFieldError("method");
                clearFieldError("chequeNumber");
                clearFieldError("amount");
              }}
              invalid={!!fieldErrors.method}
            >
              {methodCollection.items.map(item => (
                <Select.Item key={item.value} item={item} />
              ))}
            </Select.Root>
            {fieldErrors.method ? (
              <Text visual="smallRegular" color="text.error.dark" mt="-150">
                {fieldErrors.method}
              </Text>
            ) : null}
            {isComplimentary ? (
              <DateInput
                title="Accounting date"
                value={accountingDate || undefined}
                onValueChange={setAccountingDate}
                onClearDatesClick={() => setAccountingDate("")}
                clearDatesLabel="Clear date"
                canSelectPastDates
                usePortal
                fullWidth
              />
            ) : (
              <>
                <Flex gap="150">
                  <DateInput
                    title="Received date (required)"
                    value={receivedDate}
                    onValueChange={value => {
                      setReceivedDate(value);
                      clearFieldError("receivedDate");
                    }}
                    canSelectPastDates
                    usePortal
                    fullWidth
                    error={fieldErrors.receivedDate}
                  />
                  <DateInput
                    title="Accounting date"
                    value={accountingDate || undefined}
                    onValueChange={setAccountingDate}
                    onClearDatesClick={() => setAccountingDate("")}
                    clearDatesLabel="Clear date"
                    canSelectPastDates
                    usePortal
                    fullWidth
                  />
                </Flex>
                {fieldErrors.receivedDate ? (
                  <Text visual="smallRegular" color="text.error.dark" mt="-150">
                    {fieldErrors.receivedDate}
                  </Text>
                ) : null}
                <Input
                  label="Payer"
                  value={payer}
                  onChange={event => setPayer(event.target.value)}
                />
                <Input
                  label="Payment reference"
                  value={reference}
                  onChange={event => setReference(event.target.value)}
                />
                {method === "CHEQUE" ? (
                  <Input
                    label="Cheque number (required)"
                    value={chequeNumber}
                    onChange={event => {
                      setChequeNumber(event.target.value);
                      clearFieldError("chequeNumber");
                    }}
                    {...invalidInputProps(fieldErrors.chequeNumber)}
                  />
                ) : null}
                {method === "CARD" ? (
                  <>
                    <Input
                      label="Processor reference"
                      value={processorReference}
                      onChange={event =>
                        setProcessorReference(event.target.value)
                      }
                    />
                    <Flex gap="150">
                      <Input
                        label="Card brand"
                        value={cardBrand}
                        onChange={event => setCardBrand(event.target.value)}
                      />
                      <Input
                        label="Last four digits"
                        maxLength={4}
                        inputMode="numeric"
                        value={cardLast4}
                        onChange={event => {
                          setCardLast4(
                            event.target.value.replace(/\D/g, "").slice(0, 4),
                          );
                          clearFieldError("cardLast4");
                        }}
                        {...invalidInputProps(fieldErrors.cardLast4)}
                      />
                    </Flex>
                  </>
                ) : null}
              </>
            )}
            <Input
              label="Internal note"
              value={note}
              onChange={event => setNote(event.target.value)}
            />
            {settlesInvoice ? (
              <Flex alignItems="center" justifyContent="space-between" gap="400">
                <Box>
                  <Text visual="bodySemiBold">Notify customer</Text>
                  <Text visual="smallRegular" color="text.mid">
                    {notifyCustomer
                      ? "The customer gets the usual order confirmation email. Membership renewals don't send one."
                      : "No email is sent to the customer. Staff are still notified."}
                  </Text>
                </Box>
                <Switch
                  checked={notifyCustomer}
                  onCheckedChange={details =>
                    setNotifyCustomer(!!details.checked)
                  }
                />
              </Flex>
            ) : null}
          </>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
